import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const RecapPage = () => {
  return (
    <>
      <PageHeader
        overline="Recaps · The Story So Far"
        title="Every Night."
        accent="Every Story."
        subtitle="Looking back at what God is doing through RVA Students, one gathering at a time."
        testId="recap-header"
        bgImage="/images/sep26/pool-prayer.webp"
        bgPosition="center"
      />

      {/* ============ NIGHT TWO — SEPTEMBER 26 ============ */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="recap-night-two">
        <div className="glow-green top-20 -right-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-6">
            <div>
              <p className="overline mb-4">Night Two · September 26th, 2025</p>
              <h2 className="font-heading font-black text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                God did it<br />
                <span className="text-forest-glow">again</span>.
              </h2>
            </div>
            <p className="max-w-md text-white/70 text-lg leading-relaxed">
              Round two. New students, new stories, same Jesus. The backyard was full, and He
              met us there — around the pool, in prayer, in worship.
            </p>
          </div>

          {/* Video */}
          <div className="card-glass p-2 md:p-3 rounded-3xl mb-4 md:mb-6" data-testid="recap-n2-video">
            <div className="relative rounded-2xl overflow-hidden bg-black">
              <video
                src="/images/sep26/worship.mp4"
                poster="/images/sep26/worship-poster.jpg"
                controls
                playsInline
                preload="metadata"
                className="w-full block max-h-[70vh] mx-auto"
                data-testid="recap-n2-video-el"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Bento gallery */}
          <div className="grid grid-cols-12 gap-3 md:gap-5">
            <div
              className="col-span-12 md:col-span-8 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[16/10]"
              data-testid="recap-n2-photo-1"
            >
              <img
                src="/images/sep26/pool-prayer.webp"
                alt="Students gathered around the pool in prayer at Night Two"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-4 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[3/4] md:aspect-auto"
              data-testid="recap-n2-photo-2"
            >
              <img
                src="/images/sep26/girls-outdoor.webp"
                alt="Group of girls at Night Two"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-6 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[4/3]"
              data-testid="recap-n2-photo-3"
            >
              <img
                src="/images/sep26/driveway.webp"
                alt="Students greeting each other in the driveway at Night Two"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-6 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[4/3]"
              data-testid="recap-n2-photo-4"
            >
              <img
                src="/images/sep26/girls-pool.webp"
                alt="Friends smiling by the pool at Night Two"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DIVIDER */}
      <section className="relative py-8" data-testid="recap-divider">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="hr-line" />
        </div>
      </section>

      {/* ============ NIGHT ONE — JULY 31 ============ */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="recap-night-one">
        <div className="glow-blue top-20 -left-40 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-6">
            <div>
              <p className="overline mb-4">Night One · July 31st, 2025</p>
              <h2 className="font-heading font-black text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                Where it<br />
                <span className="text-deepblue-glow">all began</span>.
              </h2>
            </div>
            <p className="max-w-md text-white/70 text-lg leading-relaxed">
              74 high school students from all over Richmond showed up for the very first
              RVA Students gathering. God did more than we could have asked for or imagined.
            </p>
          </div>

          {/* Pull quote */}
          <div className="text-center mb-10 md:mb-16">
            <h3 className="font-heading font-black text-2xl sm:text-3xl md:text-4xl uppercase leading-[1.05] tracking-tight max-w-4xl mx-auto">
              "Seventy-four students from all over Richmond in{" "}
              <span className="text-forest-glow">one backyard</span>, worshipping{" "}
              <span className="text-deepblue-glow">the same Jesus</span>."
            </h3>
            <p className="mt-6 text-sm font-heading uppercase tracking-[0.24em] text-white/50">
              — Alex, Founder
            </p>
          </div>

          {/* Video */}
          <div className="card-glass p-2 md:p-3 rounded-3xl mb-4 md:mb-6" data-testid="recap-n1-video">
            <div className="relative rounded-2xl overflow-hidden bg-black">
              <video
                src="/images/july31/worship.mp4"
                poster="/images/july31/worship-poster.jpg"
                controls
                playsInline
                preload="metadata"
                className="w-full block max-h-[70vh] mx-auto"
                data-testid="recap-n1-video-el"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Photos */}
          <div className="grid grid-cols-12 gap-3 md:gap-5">
            <div
              className="col-span-12 md:col-span-8 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[16/10]"
              data-testid="recap-n1-photo-1"
            >
              <img
                src="/images/july31/welcome-sign.jpeg"
                alt="Welcome to RVA Students yard sign with balloons"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-4 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[3/4] md:aspect-auto"
              data-testid="recap-n1-photo-2"
            >
              <img
                src="/images/july31/guys-group.jpeg"
                alt="Group of high school guys at the first RVA Students gathering"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-6 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[16/10]"
              data-testid="recap-n1-photo-3"
            >
              <img
                src="/images/july31/girls-group.jpeg"
                alt="Group of high school girls at the first RVA Students gathering"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div
              className="col-span-12 md:col-span-6 relative rounded-3xl overflow-hidden border border-white/10 group aspect-[16/10]"
              data-testid="recap-n1-photo-4"
            >
              <img
                src="/images/july31/handshake.jpeg"
                alt="Students greeting each other at the entrance"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                style={{ imageOrientation: "from-image" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/10 text-center overflow-hidden" data-testid="recap-cta">
        <div className="glow-green top-0 left-1/4 opacity-30" />
        <div className="glow-blue bottom-0 right-1/4 opacity-30" />
        <div className="relative max-w-3xl mx-auto px-6">
          <p className="overline mb-6">The Story Continues</p>
          <h3 className="font-heading font-black text-4xl md:text-6xl uppercase leading-[0.95] tracking-tight mb-8">
            Two nights down.<br />
            <span className="text-forest-glow">Many</span> more coming.
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/gatherings#rsvp" className="btn-primary" data-testid="recap-rsvp-button">
              RSVP For The Next One <ArrowUpRight size={16} />
            </Link>
            <ShareButton
              label="Share The Recap"
              testId="recap-share-button"
              shareText="RVA Students — one city, one generation, one Jesus. Come to the next gathering with me:"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default RecapPage;
